import { useEffect } from 'react'
import { View, Text, Platform } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Animated from 'react-native-reanimated'
import { Car, Calendar, User, CreditCard } from '@/components/ui/icons'
import { AnimatedCheckmark } from '@/components/ui/AnimatedCheckmark'
import { notification, NotificationType } from '@/lib/utils/haptics'
import { useAuth } from '@/lib/hooks/use-auth'
import { messagingService } from '@/lib/services/messaging.service'
import { toast } from '@/lib/toast'
import { FONTS, COLORS, SPACING, RADIUS } from '@/lib/brand'
import { useSemanticColors, TEXT_STYLES } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { useBookingStore } from '@/lib/stores/booking.store'
import { useBooking } from '@/lib/hooks/use-booking'
import { useVehicleDetail } from '@/lib/hooks/use-vehicle-detail'
import { bookingDateKey, formatBookingDateTime, formatSvDateDisplay } from '@/lib/utils/dates'
import { formatCurrency } from '@/lib/utils/format'
import { Button } from '@/components/ui/Button'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { useEntrance } from '@/lib/hooks/use-entrance'

export default function ConfirmationScreen() {
  const enter = useEntrance()
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()
  const router = useRouter()
  const params = useLocalSearchParams<{ rentalId?: string }>()
  const rentalId =
    typeof params.rentalId === 'string' && params.rentalId.trim() ? params.rentalId.trim() : null

  const { user } = useAuth()
  const { vehicleId, startDate, endDate } = useBookingStore()
  const { data: booking } = useBooking(rentalId ?? undefined)
  const awaitingHost = booking?.status === 'awaiting_host'
  const confirmationPending = !!rentalId && booking?.status !== 'confirmed'
  const hostDeadline = booking?.hostResponseDeadlineAt
    ? formatBookingDateTime(booking.hostResponseDeadlineAt, undefined, {
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null
  // CON `rentalId`, MANDA EL SERVIDOR. Esto es un recibo de un cobro real.
  //
  // Antes la preferencia estaba al reves --`vehicleId ?? booking?.vehicleId`,
  // `startDate ?? bookingDateKey(booking?.startAt)`-- o sea el borrador LOCAL le
  // ganaba a la renta que se acababa de pagar. No es teorico: a esta pantalla se
  // llega con `router.replace('/booking/confirmation?rentalId=X')` desde el
  // checkout, dentro del mismo stack, y el `reset()` del layout de booking corre
  // en el DESMONTAJE del stack, asi que un borrador de OTRO carro sigue vivo. El
  // huesped pagaba el carro X y leia el recibo del carro B.
  //
  // Y `??` tampoco alcanzaba: solo cae a la derecha con null/undefined, asi que
  // una `''` persistida en un telefono de una version anterior GANA. El store se
  // persiste sin `migrate`, y `''` es justo lo que escribia `setDates(date, '')`
  // en todos los builds anteriores a hoy: normalizar el camino de escritura no
  // alcanza a lo que ya esta guardado en los telefonos.
  //
  // Mismo patron que ya usa el checkout en su camino de recuperacion
  // (checkout.tsx: `recoveryRentalId ? bookingDateKey(recoveredBooking?.startAt)
  // : startDate`). Mientras la consulta viaja no se dibuja la tarjeta, que es
  // preferible a dibujar el recibo equivocado.
  const activeVehicleId = rentalId ? (booking?.vehicleId ?? null) : (vehicleId ?? null)
  const { vehicle } = useVehicleDetail(activeVehicleId ?? undefined)
  const displayVehicleTitle = vehicle?.title ?? booking?.vehicleTitle ?? null
  const displayStartDate = rentalId
    ? (bookingDateKey(booking?.startAt) ?? null)
    : (startDate ?? null)
  const displayEndDate = rentalId ? (bookingDateKey(booking?.endAt) ?? null) : (endDate ?? null)
  const displayHostName = vehicle?.host?.displayName ?? booking?.hostDisplayName ?? null
  // Every row in the details card is conditional, so when the booking query
  // has not resolved (or carries none of these fields) the card rendered as an
  // empty bordered capsule between the hero and "¿Qué sigue?" — a component
  // that looks broken. No rows, no card.
  const hasBookingDetails = Boolean(
    displayVehicleTitle ||
    (displayStartDate && displayEndDate) ||
    displayHostName ||
    booking?.pricing,
  )

  // Haptic success on mount
  useEffect(() => {
    if (Platform.OS !== 'web') {
      void notification(NotificationType.Success)
    }
  }, [])

  const handleViewTrips = () => {
    useBookingStore.getState().reset()
    router.replace('/(tabs)/trips')
  }

  const handleContactHost = async () => {
    const hostId = vehicle?.host?.id ?? booking?.hostId
    if (!user || !hostId) return
    try {
      const conversationId = await messagingService.getOrCreateConversation(user.id, hostId)
      router.push(`/(tabs)/messages/${conversationId}`)
    } catch (err) {
      if (__DEV__) console.error('[Confirmation] contact host failed:', err)
      toast.error(t('common.error'))
    }
  }

  return (
    <ScreenContainer card={false} scroll={false}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          flexGrow: 1,
          paddingBottom: insets.bottom + SPACING.lg,
          paddingHorizontal: SPACING.md,
        }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {/* Success icon + title */}
        <Animated.View
          entering={enter()}
          collapsable={false}
          style={{ alignItems: 'center', marginTop: SPACING.xl, gap: SPACING.md }}
        >
          {/* Animated celebration check — SVG stroke-draw + spring + haptic
              already fires from useEffect above. Bigger than the prior 80px
              static icon so the moment lands. */}
          <AnimatedCheckmark size={104} color={colors.success} />

          <Text
            style={{
              // Heavitas — completing a booking is the single most
              // emotionally-loaded moment in the app. The brand display
              // face turns "Booking confirmed" from a generic success
              // string into a Tago-specific moment.
              ...TEXT_STYLES.display,
              fontSize: 28,
              lineHeight: 34,
              color: colors.textPrimary,
              textAlign: 'center',
            }}
          >
            {confirmationPending
              ? t(awaitingHost ? 'host_approval.waiting_title' : 'host_approval.payment_received')
              : t('confirmation.title')}
          </Text>

          <Text
            style={{
              fontSize: 14,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
              textAlign: 'center',
            }}
          >
            {confirmationPending ? t('host_approval.waiting_subtitle') : t('confirmation.subtitle')}
          </Text>
        </Animated.View>

        {/* Booking details card */}
        {hasBookingDetails && (
          <Animated.View
            entering={enter(3)}
            collapsable={false}
            style={{
              marginTop: SPACING.lg,
              padding: SPACING.md,
              backgroundColor: colors.surface,
              borderRadius: RADIUS.lg,
              borderWidth: 1,
              borderColor: colors.border,
              gap: SPACING.md,
            }}
          >
            {/* Vehicle */}
            {displayVehicleTitle ? (
              <DetailRow
                icon={<Car size={18} color={colors.textSecondary} />}
                label={t('confirmation.vehicle')}
                value={displayVehicleTitle}
                colors={colors}
              />
            ) : null}

            {/* Dates */}
            {displayStartDate && displayEndDate ? (
              <DetailRow
                icon={<Calendar size={18} color={colors.textSecondary} />}
                label={t('confirmation.dates')}
                value={`${formatSvDateDisplay(displayStartDate)} — ${formatSvDateDisplay(displayEndDate)}`}
                colors={colors}
              />
            ) : null}

            {/* Host */}
            {displayHostName ? (
              <DetailRow
                icon={<User size={18} color={colors.textSecondary} />}
                label={t('confirmation.host')}
                value={displayHostName}
                colors={colors}
              />
            ) : null}

            {/* This screen is the receipt for a charge that just happened, and it
              never said how much. The booking query already carries the
              breakdown — same source checkout.tsx prices from. */}
            {booking?.pricing && (
              <DetailRow
                icon={<CreditCard size={18} color={colors.textSecondary} />}
                label={t('confirmation.total_paid')}
                value={formatCurrency(booking.pricing.totalCents)}
                colors={colors}
              />
            )}
          </Animated.View>
        )}

        {/* What's next */}
        <Animated.View
          entering={enter(6)}
          collapsable={false}
          style={{
            marginTop: SPACING.lg,
            padding: SPACING.md,
            backgroundColor: colors.surface,
            borderRadius: RADIUS.lg,
            borderWidth: 1,
            borderColor: colors.border,
          }}
        >
          <Text
            style={{
              fontSize: 18,
              fontFamily: FONTS.bodyBold,
              color: colors.textPrimary,
              marginBottom: SPACING.md,
            }}
          >
            {t('confirmation.what_next')}
          </Text>

          {confirmationPending ? (
            <>
              <NextStep
                number={1}
                text={
                  hostDeadline
                    ? t('host_approval.guest_deadline', { time: hostDeadline })
                    : t('host_approval.guest_body')
                }
                colors={colors}
              />
              <NextStep number={2} text={t('confirmation.next_step_2')} colors={colors} />
              <NextStep number={3} text={t('host_approval.guest_body')} colors={colors} />
            </>
          ) : (
            <>
              <NextStep number={1} text={t('confirmation.next_step_1')} colors={colors} />
              <NextStep number={2} text={t('confirmation.next_step_2')} colors={colors} />
              <NextStep number={3} text={t('confirmation.next_step_3')} colors={colors} />
            </>
          )}
        </Animated.View>

        {/* Pushes the actions onto the bottom safe area when the confirmation
            fits on one screen, and collapses to nothing when it doesn't — the
            CTA used to float mid-screen over a third of empty background. */}
        <View style={{ flex: 1 }} />

        {/* Action buttons */}
        <Animated.View
          entering={enter(9)}
          collapsable={false}
          style={{ marginTop: SPACING.lg, gap: SPACING.md }}
        >
          <Button
            title={t('confirmation.view_trips')}
            variant="primary"
            size="lg"
            onPress={handleViewTrips}
            testID="confirmation.view-trips-button"
            accessibilityRole="button"
            accessibilityLabel={t('confirmation.view_trips')}
          />

          {(vehicle?.host?.id ?? booking?.hostId) ? (
            <Button
              title={t('confirmation.contact_host')}
              variant="secondary"
              size="lg"
              onPress={handleContactHost}
              testID="confirmation.contact-host-button"
              accessibilityRole="button"
              accessibilityLabel={t('confirmation.contact_host')}
            />
          ) : null}
        </Animated.View>
      </ScrollView>
    </ScreenContainer>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function DetailRow({
  icon,
  label,
  value,
  colors,
}: {
  icon: React.ReactNode
  label: string
  value: string
  colors: ReturnType<typeof useSemanticColors>
}) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
      {icon}
      <View style={{ flex: 1 }}>
        <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textMuted }}>
          {label}
        </Text>
        <Text
          style={{ fontSize: 16, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}
          numberOfLines={2}
        >
          {value}
        </Text>
      </View>
    </View>
  )
}

function NextStep({
  number,
  text,
  colors,
}: {
  number: number
  text: string
  colors: ReturnType<typeof useSemanticColors>
}) {
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.sm,
        marginBottom: SPACING.sm,
      }}
    >
      <View
        style={{
          width: 24,
          height: 24,
          borderRadius: RADIUS.md, // circle = 24 / 2
          backgroundColor: COLORS.coral.dark,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text style={{ fontSize: 12, fontFamily: FONTS.bodyBold, color: '#FFFFFF' }}>{number}</Text>
      </View>
      <Text style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textSecondary, flex: 1 }}>
        {text}
      </Text>
    </View>
  )
}
